import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Alert, LinearProgress } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import edgeFunctionService from '../../services/edgeFunctionService';
import { useAIRequests } from '../../context/AIRequestContext';
import { useAuth } from '../../context/EnterpriseAuthContext';

/**
 * Kick off an async PowerPoint template import.
 *
 * Creates an `ai_requests` row (`type='template_import'`), dispatches the file to the
 * `import-presentation-template` Edge function, and navigates back to the Templates list.
 * The user monitors progress in the AI jobs notification panel; clicking the completed
 * task opens the new draft template directly in the rich editor.
 *
 * @param {object} [props] - Props (all optional).
 * @param {function(): void} [props.onDispatched] - Called after the job has been queued.
 * @returns {React.ReactElement}
 */
function PowerPointUploadSection({ onDispatched }) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { createRequest, refreshRequests } = useAIRequests();
  const { state: authState } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadInfo, setUploadInfo] = useState('');

  /**
   * Read file as base64 (no data URL prefix).
   * @param {File} file - PPTX file.
   * @returns {Promise<string>}
   */
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') {
          reject(new Error('Could not read file'));
          return;
        }
        const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  /**
   * Validate + dispatch the pptx to the async import pipeline.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event.
   * @returns {Promise<void>}
   */
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pptx')) {
      setUploadError('Please choose a .pptx file.');
      return;
    }
    // Keep under Supabase Edge Functions' request-body cap once base64-encoded (~1.33x expansion).
    const maxBytes = 14 * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadError('File is too large (max 14MB). Please trim media or export a lighter .pptx.');
      return;
    }

    const accountId = authState?.activeAccount?.id;
    if (!accountId) {
      setUploadError('No active account — cannot queue the import.');
      return;
    }

    setUploadError('');
    setUploadInfo('');
    setUploading(true);

    try {
      const b64 = await fileToBase64(file);
      const aiRequest = await createRequest({
        type: 'template_import',
        title: `Importing ${file.name}`,
        data: { fileName: file.name, accountId },
      });
      if (!aiRequest?.id) {
        throw new Error('Could not create the import job.');
      }
      await edgeFunctionService.importPresentationTemplate({
        requestId: aiRequest.id,
        fileBase64: b64,
        fileName: file.name,
        accountId,
      });
      await refreshRequests();
      setUploadInfo(
        `Import started for ${file.name}. Check the jobs panel in the navbar — we'll open the draft template when it's ready.`
      );
      if (typeof onDispatched === 'function') {
        onDispatched();
      }
      // Take the user back to the templates list so the jobs tracker is visible.
      setTimeout(() => navigate('/templates'), 800);
    } catch (err) {
      console.error('PPTX import dispatch failed:', err);
      setUploadError(err?.message || 'Could not start the import. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <input
        ref={inputRef}
        type="file"
        accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        hidden
        onChange={handleFile}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFile />}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        Import from .pptx
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Runs in the background: Inky cleans up the slide text, restructures it and turns it into
        meaningful <code>{'{{variables}}'}</code>. The jobs panel in the header tells you when the
        draft is ready for review.
      </Typography>
      {uploading && <LinearProgress sx={{ mt: 1 }} />}
      {uploadInfo && !uploadError && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {uploadInfo}
        </Alert>
      )}
      {uploadError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
}

export default PowerPointUploadSection;
